/**
 * My Favorites Menu Component
 * 
 * Context menu for user's favorited layouts from the database
 * Fetches layouts favorited by the logged-in user and displays them in categories
 */

/**
 * Convert 18-element TroopNumbers array (database format) to 17-element layoutTroops array (game format)
 * Removes the Senator (index 17) to match the game's format
 * @param {number[]|string} troopNumbers - 18-element array or JSON string
 * @returns {number[]|null} 17-element array or null if invalid
 */
function convertTroopNumbersToLayoutTroops(troopNumbers) {
    if (!troopNumbers) return null;
    
    let array = troopNumbers;
    if (typeof troopNumbers === 'string') {
        try {
            array = JSON.parse(troopNumbers);
        } catch (e) {
            console.error('Failed to parse TroopNumbers:', e);
            return null;
        }
    }
    
    if (!Array.isArray(array)) return null;
    
    // If already 17 elements, return as-is (already in game format)
    if (array.length === 17) return array;
    
    // If 18 elements, remove the last one (Senator at index 17)
    if (array.length === 18) {
        return array.slice(0, 17);
    }
    
    // If not 17 or 18, return null (invalid format)
    return null;
}

export class MyFavoritesMenu {
    constructor() {
        this.element = null;
        this.isOpen = false;
        this.onLoadLayout = null;
        this.layouts = {}; // Will be organized by category (mapType)
        this.isLoading = false;
        this.currentUsername = null;
        
        this.createMenu();
        this.checkAuthStatus();
    }
    
    /**
     * Check if user is logged in and get username
     */
    async checkAuthStatus() {
        try {
            const apiBase = this.getApiBase();
            const response = await fetch(`${apiBase}auth/check.php`);
            
            // Check if response is JSON before parsing
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                // Not JSON (probably HTML 404 page) - treat as not logged in
                this.showNotLoggedIn();
                return;
            }
            
            const data = await response.json();
            if (data.loggedIn && data.user) {
                this.currentUsername = data.user.username;
                await this.loadFavorites();
            } else {
                this.showNotLoggedIn();
            }
        } catch (error) {
            // Silently fail - auth endpoint may not exist yet
            this.showNotLoggedIn();
        }
    }
    
    /**
     * Load favorited layouts for the current user
     */
    async loadFavorites() {
        if (!this.currentUsername) {
            return;
        }
        
        this.isLoading = true;
        this.updateMenuContent('<div class="layout-menu-loading">Loading your favorites...</div>');
        
        try {
            const apiBase = this.getApiBase();
            
            // First check for custom folders
            const foldersResponse = await fetch(`${apiBase}user/folders.php?type=favorites`);
            const foldersData = await foldersResponse.json();
            
            if (foldersData.success && foldersData.folders && foldersData.folders.length > 0) {
                // Use custom folder structure
                this.layouts = this.convertFoldersToMenuStructure(foldersData.folders);
                this.rebuildMenuFromFolders();
            } else {
                // Fall back to auto-generated categories
                const response = await fetch(`${apiBase}layouts/favorites.php?limit=1000`);
                const data = await response.json();
                
                if (data.success && data.favorites && data.favorites.length > 0) {
                    // Organize layouts by mapType (category)
                    this.layouts = {};
                    data.favorites.forEach(layout => {
                        const category = layout.mapType || 'General';
                        if (!this.layouts[category]) {
                            this.layouts[category] = [];
                        }
                        this.layouts[category].push(layout);
                    });
                    
                    this.rebuildMenu();
                } else {
                    this.showNoFavorites();
                }
            }
        } catch (error) {
            console.error('Failed to load favorites:', error);
            this.updateMenuContent('<div class="layout-menu-error">Failed to load favorites</div>');
        } finally {
            this.isLoading = false;
        }
    }
    
    /**
     * Convert folder structure to menu structure
     */
    convertFoldersToMenuStructure(folders) {
        const structure = {};
        
        const processFolder = (folder) => {
            if (!structure[folder.name]) {
                structure[folder.name] = [];
            }
            
            // Add items in this folder
            if (folder.items && folder.items.length > 0) {
                folder.items.forEach(item => {
                    structure[folder.name].push(item);
                });
            }
            
            // Process child folders
            if (folder.children && folder.children.length > 0) {
                folder.children.forEach(child => {
                    processFolder(child);
                });
            }
        };
        
        folders.forEach(folder => {
            processFolder(folder);
        });
        
        return structure;
    }
    
    /**
     * Rebuild menu from folder structure (supports nested folders)
     */
    rebuildMenuFromFolders() {
        if (Object.keys(this.layouts).length === 0) {
            this.showNoFavorites();
            return;
        }
        
        // Build category structure from folders
        const categoryItems = Object.keys(this.layouts).map((categoryName, categoryIndex) => {
            const layouts = this.layouts[categoryName];
            const layoutItems = layouts.map((layout, layoutIndex) => {
                const uniqueId = `fav-layout-${categoryIndex}-${layoutIndex}`;
                const layoutName = layout.LayoutName || `Layout ${layoutIndex + 1}`;
                return `
                    <button 
                        id="${uniqueId}" 
                        class="layout-menu-item" 
                        data-category="${categoryIndex}"
                        data-layout="${layoutIndex}"
                        title="${layout.Remarks || layout.Notes || ''}">
                        ${this.escapeHtml(layoutName)}
                    </button>
                `;
            }).join('');
            
            return `
                <div class="layout-menu-category">
                    <div class="layout-menu-category-header" id="fav-layout-category-${categoryIndex}">
                        ${this.escapeHtml(categoryName)} ▶
                    </div>
                    <div class="layout-menu-submenu" id="fav-layout-submenu-${categoryIndex}" style="display: none;">
                        ${layoutItems}
                    </div>
                </div>
            `;
        }).join('');
        
        this.updateMenuContent(categoryItems);
        
        // Setup event listeners for category headers
        Object.keys(this.layouts).forEach((categoryName, categoryIndex) => {
            const header = document.getElementById(`fav-layout-category-${categoryIndex}`);
            const submenu = document.getElementById(`fav-layout-submenu-${categoryIndex}`);
            
            if (header && submenu) {
                header.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const isVisible = submenu.style.display !== 'none';
                    submenu.style.display = isVisible ? 'none' : 'block';
                    header.textContent = isVisible ? `${categoryName} ▶` : `${categoryName} ▼`;
                });
            }
        });
        
        // Setup event listeners for layout items
        Object.keys(this.layouts).forEach((categoryName, categoryIndex) => {
            const layouts = this.layouts[categoryName];
            layouts.forEach((layout, layoutIndex) => {
                const btn = document.getElementById(`fav-layout-${categoryIndex}-${layoutIndex}`);
                if (btn) {
                    btn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        if (this.onLoadLayout) {
                            // Convert database layout to format expected by MainApp
                            const layoutData = {
                                layoutName: layout.LayoutName,
                                layoutString: layout.LongLink || layout.Sharestring || '',
                                layoutRemark: layout.Remarks || '',
                                layoutNote: layout.Notes || '',
                                layoutTroops: convertTroopNumbersToLayoutTroops(layout.TroopNumbers),
                                layoutResources: layout.LayoutResources ? JSON.parse(layout.LayoutResources) : null,
                                mapType: layout.mapType,
                                // Include full layout object for metadata
                                _dbLayout: layout
                            };
                            this.onLoadLayout(layoutData);
                        }
                        this.hide();
                    });
                }
            });
        });
    }
    
    /**
     * Show message when user is not logged in
     */
    showNotLoggedIn() {
        this.updateMenuContent(`
            <div class="layout-menu-message">
                <p>Please log in to view your favorites.</p>
            </div>
        `);
    }
    
    /**
     * Show message when user has no favorites
     */
    showNoFavorites() {
        this.updateMenuContent(`
            <div class="layout-menu-message">
                <p>You haven't favorited any layouts yet.</p>
                <p style="font-size: 0.9em; color: var(--text-secondary, #999); margin-top: 0.5em;">
                    Click the star icon on any layout to add it to your favorites.
                </p>
            </div>
        `);
    }
    
    /**
     * Update menu content
     */
    updateMenuContent(html) {
        if (this.element) {
            const content = this.element.querySelector('.layout-menu-content');
            if (content) {
                content.innerHTML = html;
            }
        }
    }
    
    /**
     * Rebuild the menu with loaded layouts
     */
    rebuildMenu() {
        if (Object.keys(this.layouts).length === 0) {
            this.showNoFavorites();
            return;
        }
        
        // Build category structure (similar to LayoutMenu)
        const categoryItems = Object.keys(this.layouts).map((categoryName, categoryIndex) => {
            const layouts = this.layouts[categoryName];
            const layoutItems = layouts.map((layout, layoutIndex) => {
                const uniqueId = `fav-layout-${categoryIndex}-${layoutIndex}`;
                const layoutName = layout.LayoutName || `Layout ${layoutIndex + 1}`;
                return `
                    <button 
                        id="${uniqueId}" 
                        class="layout-menu-item" 
                        data-category="${categoryIndex}"
                        data-layout="${layoutIndex}"
                        title="${layout.Remarks || layout.Notes || ''}">
                        ${this.escapeHtml(layoutName)}
                    </button>
                `;
            }).join('');
            
            return `
                <div class="layout-menu-category">
                    <div class="layout-menu-category-header" id="fav-layout-category-${categoryIndex}">
                        ${this.escapeHtml(categoryName)} ▶
                    </div>
                    <div class="layout-menu-submenu" id="fav-layout-submenu-${categoryIndex}" style="display: none;">
                        ${layoutItems}
                    </div>
                </div>
            `;
        }).join('');
        
        this.updateMenuContent(categoryItems);
        
        // Setup event listeners for category headers
        Object.keys(this.layouts).forEach((categoryName, categoryIndex) => {
            const header = document.getElementById(`fav-layout-category-${categoryIndex}`);
            const submenu = document.getElementById(`fav-layout-submenu-${categoryIndex}`);
            
            if (header && submenu) {
                header.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const isVisible = submenu.style.display !== 'none';
                    submenu.style.display = isVisible ? 'none' : 'block';
                    header.textContent = isVisible ? `${categoryName} ▶` : `${categoryName} ▼`;
                });
            }
        });
        
        // Setup event listeners for layout items
        Object.keys(this.layouts).forEach((categoryName, categoryIndex) => {
            const layouts = this.layouts[categoryName];
            layouts.forEach((layout, layoutIndex) => {
                const btn = document.getElementById(`fav-layout-${categoryIndex}-${layoutIndex}`);
                if (btn) {
                    btn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        if (this.onLoadLayout) {
                            // Convert database layout to format expected by MainApp
                            const layoutData = {
                                layoutName: layout.LayoutName,
                                layoutString: layout.LongLink || layout.Sharestring || '',
                                layoutRemark: layout.Remarks || '',
                                layoutNote: layout.Notes || '',
                                layoutTroops: convertTroopNumbersToLayoutTroops(layout.TroopNumbers),
                                layoutResources: layout.LayoutResources ? JSON.parse(layout.LayoutResources) : null,
                                mapType: layout.mapType,
                                // Include full layout object for metadata
                                _dbLayout: layout
                            };
                            this.onLoadLayout(layoutData);
                        }
                        this.hide();
                    });
                }
            });
        });
    }
    
    /**
     * Create the menu element
     */
    createMenu() {
        const menu = document.createElement('div');
        menu.className = 'layout-menu';
        menu.id = 'my-favorites-menu';
        menu.style.display = 'none';
        menu.innerHTML = `
            <div class="layout-menu-content"></div>
        `;
        document.body.appendChild(menu);
        this.element = menu;
    }
    
    /**
     * Show the menu at specified position
     */
    show(x, y) {
        if (!this.element) {
            this.createMenu();
        }
        
        // Reload favorites when showing menu
        if (this.currentUsername) {
            this.loadFavorites();
        } else {
            this.checkAuthStatus();
        }
        
        this.element.style.display = 'block';
        this.element.style.left = x + 'px';
        this.element.style.top = y + 'px';
        this.isOpen = true;
        
        // Close on outside click
        setTimeout(() => {
            document.addEventListener('click', this.handleOutsideClick.bind(this), { once: true });
        }, 0);
    }
    
    /**
     * Hide the menu
     */
    hide() {
        if (this.element) {
            this.element.style.display = 'none';
        }
        this.isOpen = false;
    }
    
    /**
     * Toggle menu visibility
     */
    toggle(x, y) {
        if (this.isOpen) {
            this.hide();
        } else {
            this.show(x, y);
        }
    }
    
    /**
     * Handle clicks outside the menu
     */
    handleOutsideClick(e) {
        if (this.element && !this.element.contains(e.target)) {
            this.hide();
        }
    }
    
    /**
     * Get API base URL
     */
    getApiBase() {
        const scriptPath = window.location.pathname;
        const hostname = window.location.hostname;

        if (hostname.includes('cotg.rlmglobal.com') ||
            (hostname.includes('cotgopt.com') && !scriptPath.includes('/cotgopt/'))) {
            return '/api/';
        }

        if (scriptPath.includes('/Louopt/')) {
            return '/Louopt/api/';
        } else if (scriptPath.includes('/cotgopt/')) {
            return '/cotgopt/api/';
        } else if (scriptPath.includes('/cotg/')) {
            return '/cotg/api/';
        }
        return '/api/';
    }
    
    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
        if (!text) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

